import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Lock, Loader2, CheckCircle, XCircle } from "lucide-react";

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Password validation
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
  };
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast.error("Please ensure your password meets all requirements");
      return;
    }
    
    if (!passwordsMatch) {
      toast.error("Passwords do not match");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center bg-slate-900 relative"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1705417272217-490f4511abeb?w=1920&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" />
        
        <Card className="w-full max-w-md mx-4 relative z-10 border-slate-200 shadow-2xl">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Invalid Link</h2>
            <p className="text-slate-500 mb-6">
              This password reset link is invalid or has expired.
            </p>
            <Link to="/forgot-password">
              <Button className="w-full">Request New Link</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center bg-slate-900 relative"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1705417272217-490f4511abeb?w=1920&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" />
        
        <Card className="w-full max-w-md mx-4 relative z-10 border-slate-200 shadow-2xl">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Password Reset!</h2>
            <p className="text-slate-500 mb-6">
              Your password has been successfully reset. You can now sign in with your new password.
            </p>
            <Button 
              className="w-full"
              onClick={() => navigate("/login")}
            >
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-slate-900 relative"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1705417272217-490f4511abeb?w=1920&q=80')",
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
    >
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" />
      
      <Card className="w-full max-w-md mx-4 relative z-10 border-slate-200 shadow-2xl" data-testid="reset-password-card">
        <CardHeader className="space-y-1 text-center pb-2">
          <div className="w-14 h-14 bg-orange-600 rounded-md mx-auto mb-4 flex items-center justify-center">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight font-heading">
            Create New Password
          </CardTitle>
          <CardDescription className="text-slate-500">
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="password"
                placeholder="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 h-11"
                data-testid="password-input"
                required
              />
            </div>
            
            {/* Password requirements */}
            {password && (
              <div className="space-y-1 text-sm">
                <PasswordCheck passed={passwordChecks.length} label="At least 8 characters" />
                <PasswordCheck passed={passwordChecks.uppercase} label="One uppercase letter" />
                <PasswordCheck passed={passwordChecks.lowercase} label="One lowercase letter" />
                <PasswordCheck passed={passwordChecks.number} label="One number" />
              </div>
            )}
            
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`pl-10 h-11 ${confirmPassword && !passwordsMatch ? 'border-red-500' : ''}`}
                data-testid="confirm-password-input"
                required
              />
            </div>
            {confirmPassword && !passwordsMatch && (
              <p className="text-red-500 text-sm">Passwords do not match</p>
            )}
            
            <Button 
              type="submit" 
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium"
              disabled={loading || !isPasswordValid || !passwordsMatch}
              data-testid="reset-password-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Resetting...
                </span>
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const PasswordCheck = ({ passed, label }) => (
  <div className={`flex items-center gap-2 ${passed ? 'text-emerald-600' : 'text-slate-400'}`}>
    {passed ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
    <span>{label}</span>
  </div>
);

export default ResetPasswordPage;
